/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Chip from '@mui/material/Chip';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import TableHeader from 'components/Table/TableHeader';
import MDDateRange from 'components/MDDateRange';

// Material Dashboard 2 PRO React examples
import Navbar from 'examples/Navbars/NavBar';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';
import { useParams } from 'react-router-dom';

const initFind = {
  search: '',
  order_by: 'tb.id',
  order_sort: 'desc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
  period: 'ALL',
  branch: 'ALL',
  checklist_id: 'ALL',
  check_type_id: 'ALL',
  status: 'ALL',
  severity: 'ALL',
  date_start: Func.dateTimeFirstMonth(),
  date_stop: Func.dateTimeEndMonth(),
};

function RpProductChecklist() {
  const mod = 'rp_checklist_product';
  const { id } = useParams();
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [cboBranch, setCboBranch] = useState([]);
  const [cboCheckTypes, setCboCheckTypes] = useState([]);
  const [cboChecklists, setCboChecklists] = useState([]);
  const [urlExcel, setUrlExcel] = useState('');
  const [previewImage, setPreviewImage] = useState(null);

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: 'tb.item_type', label: 'ประเภท', align: 'center', width: 80 },
    { id: 'pd.name', label: 'สินค้า / ชั้นวาง', align: 'left' },
    { id: 'tb.checklist_id', label: 'รายการตรวจสอบ', align: 'left' },
    { id: 'tb.check_type_id', label: 'ประเภทการตรวจ', align: 'center' },
    { id: 'tb.option_label', label: 'ผลการตรวจ', align: 'center' },
    { id: 'tb.branch_id', label: 'สาขา', align: 'left' },
    { id: 'tb.item_status', label: 'สถานะ', align: 'center', width: 100 },
    { id: 'tb.checker_name', label: 'ผู้ตรวจสอบ', align: 'left' },
    { id: 'tb.note', label: 'หมายเหตุ', align: 'left' },
    { id: 'tb.check_date', label: 'วันที่ตรวจสอบ', align: 'center', width: 150 },
    { id: 'tb.image', label: 'รูปภาพ', align: 'center', width: 80, disableSort: true },
  ];

  const statusColors = {
    checked: 'success',
    unchecked: 'warning',
    skipped: 'default',
  };

  const severityColors = {
    NORMAL: 'success',
    WARNING: 'warning',
    CRITICAL: 'error',
  };

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);
    closeShow();

    const requestBody = {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      branch_id: data.branch === 'ALL' ? undefined : data.branch,
      checklist_id: data.checklist_id === 'ALL' ? undefined : data.checklist_id,
      check_type_id: data.check_type_id === 'ALL' ? undefined : data.check_type_id,
      status: data.status === 'ALL' ? undefined : data.status,
      severity: data.severity === 'ALL' ? undefined : data.severity,
    };

    if (data.period !== 'ALL') {
      requestBody.date_from = data.date_start;
      requestBody.date_to = data.date_stop;
    }

    FetchData('POST', `/${mod}`, app.token, requestBody).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data || []);
        setPageTotal(result.total_pages || 0);
        setPageNo(result.page || 1);
      }
      setLoading(false);
    });
  };

  const excel = () => {
    let param = `download`;
    param += `?search=${find.search}`;
    param += find.branch !== 'ALL' ? `&branch_id=${find.branch}` : '';
    param += find.checklist_id !== 'ALL' ? `&checklist_id=${find.checklist_id}` : '';
    param += find.check_type_id !== 'ALL' ? `&check_type_id=${find.check_type_id}` : '';
    param += find.status !== 'ALL' ? `&status=${find.status}` : '';
    param += find.severity !== 'ALL' ? `&severity=${find.severity}` : '';
    param += find.period === 'ALL' ? '' : `&date_from=${find.date_start}`;
    param += find.period === 'ALL' ? '' : `&date_to=${find.date_stop}`;

    window.open(`${urlExcel}/${param}`, '_self');
  };

  const setDateRange = (start, stop) => {
    view({
      ...find,
      date_start: Func.dateTimeFormat(start.toDate()),
      date_stop: Func.dateTimeFormat(stop.toDate()),
      page: 1,
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 50 })}>
        {t(`element.show_record`, { num: 50 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);

    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.branch}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, branch: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{t('menu.branch')} :</InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              {cboBranch.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.checklist_id}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, checklist_id: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: <InputAdornment position='start'>รายการตรวจ :</InputAdornment>,
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              {cboChecklists.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.code} - {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.check_type_id}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, check_type_id: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: <InputAdornment position='start'>ประเภท :</InputAdornment>,
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              {cboCheckTypes.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.status}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, status: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: <InputAdornment position='start'>สถานะ :</InputAdornment>,
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              <MenuItem value='checked'>ตรวจแล้ว</MenuItem>
              <MenuItem value='unchecked'>ยังไม่ตรวจ</MenuItem>
              <MenuItem value='skipped'>ข้ามรายการ</MenuItem>
            </MDInput>
          </Grid>

          {find.period === 'ALL' ? (
            <Grid item xs={12} sm={12} md={6} lg={2}>
              <MDButton
                variant='outlined'
                color='secondary'
                fullWidth
                sx={{ p: 1.5 }}
                onClick={() => view({ ...find, period: 'DATE', page: 1 })}
              >
                <Icon>edit_calendar</Icon>&nbsp;เลือกช่วงเวลา
              </MDButton>
            </Grid>
          ) : null}
          {find.period === 'ALL' ? null : (
            <>
              <Grid item xs={12} sm={12} md={12} lg={3}>
                <MDDateRange
                  dateStart={find.date_start}
                  dateStop={find.date_stop}
                  callBack={setDateRange}
                  closeDate={() => view({ ...find, period: 'ALL', page: 1 })}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={1}>
                <MDBox display='flex' justifyContent='end' gap={1}>
                  <MDButton variant='gradient' color='success' onClick={excel}>
                    <Icon>table_view</Icon>&nbsp;Excel
                  </MDButton>
                </MDBox>
              </Grid>
            </>
          )}
          <Grid item xs={12} sm={12} md={12} lg={1}>
            <MDBox display='flex' justifyContent='end' gap={1}>
              <MDButton variant='gradient' color='success' onClick={excel}>
                <Icon>table_view</Icon>&nbsp;Excel
              </MDButton>
            </MDBox>
          </Grid>
        </Grid>
      </Card>
    );
  };

  const renderStatusChip = (status) => {
    const statusLabels = {
      checked: 'ตรวจแล้ว',
      unchecked: 'ยังไม่ตรวจ',
      skipped: 'ข้ามรายการ',
    };

    return (
      <Chip
        label={statusLabels[status] || status}
        color={statusColors[status] || 'default'}
        size='small'
      />
    );
  };

  const renderSeverityChip = (severity) => {
    const severityLabels = {
      NORMAL: 'ปกติ',
      WARNING: 'เตือน',
      CRITICAL: 'วิกฤต',
    };

    return (
      <Chip
        label={severityLabels[severity] || severity}
        color={severityColors[severity] || 'default'}
        size='small'
      />
    );
  };

  const renderItemTypeChip = (itemType) => {
    const itemTypeLabels = {
      shelf: 'ชั้นวาง',
      product: 'สินค้า',
    };

    return itemTypeLabels[itemType] || itemType;
  };

  const renderOptionChip = (row) => {
    if (!row.option_label) return '-';

    return row.option_label;
  };

  const renderImage = (imageUrl) => {
    if (!imageUrl) return '-';
    return (
      <MDBox
        component='img'
        src={imageUrl}
        alt='checklist'
        sx={{
          width: 48,
          height: 48,
          objectFit: 'cover',
          borderRadius: 1,
          cursor: 'pointer',
          border: '1px solid #e0e0e0',
          '&:hover': { opacity: 0.8 },
        }}
        onClick={() => setPreviewImage(imageUrl)}
      />
    );
  };

  const renderImagePreview = () => (
    <Dialog open={Boolean(previewImage)} onClose={() => setPreviewImage(null)} maxWidth='md'>
      <DialogContent sx={{ p: 1, position: 'relative' }}>
        <IconButton
          onClick={() => setPreviewImage(null)}
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            zIndex: 1,
            bgcolor: 'rgba(0,0,0,0.4)',
            color: '#fff',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
          }}
          size='small'
        >
          <CloseIcon fontSize='small' />
        </IconButton>
        {previewImage && (
          <MDBox
            component='img'
            src={previewImage}
            alt='preview'
            sx={{ maxWidth: '80vw', maxHeight: '80vh', display: 'block', borderRadius: 1 }}
          />
        )}
      </DialogContent>
    </Dialog>
  );

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <MDButton
              variant='contained'
              color='dark'
              size='large'
              fullWidth
              sx={{ pt: 1.6, pb: 1.6 }}
              href='/report_account'
            >
              <Icon>content_paste_search</Icon>
            </MDButton>
          </Grid>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper} sx={{ minHeight: 400 }}>
              <Table aria-label='datatable table'>
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleSort}
                  data={initHeader}
                />
                <TableBody>
                  {record.map((row, idx) => (
                    <TableRow
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${row.checklist_id}-${row.check_type_code}-${
                        row.barcode || row.shelf_name
                      }-${idx}`}
                    >
                      <TableCell align='center'>{renderItemTypeChip(row.item_type)}</TableCell>
                      <TableCell align='left'>
                        {row.item_type === 'shelf' ? (
                          <MDBox className='nowrap'>{row.shelf_name || '-'}</MDBox>
                        ) : (
                          <>
                            <MDBox>{row.product_code}</MDBox>
                            <MDBox className='nowrap'>{row.product_name}</MDBox>
                          </>
                        )}
                      </TableCell>
                      <TableCell align='left'>
                        <MDBox className='nowrap'>{row.checklist_code}</MDBox>
                        <MDBox className='nowrap'>{row.checklist_name}</MDBox>
                      </TableCell>
                      <TableCell align='center'>
                        <MDBox className='nowrap'>{row.check_type_name}</MDBox>
                      </TableCell>
                      <TableCell align='center'>
                        <MDBox display='flex' flexDirection='column' alignItems='center' gap={0.5}>
                          {renderOptionChip(row)}
                        </MDBox>
                      </TableCell>
                      <TableCell align='left'>
                        <MDBox className='nowrap'>{row.branch_name}</MDBox>
                      </TableCell>
                      <TableCell align='center'>{renderStatusChip(row.item_status)}</TableCell>
                      <TableCell align='left'>{row.checker_name}</TableCell>
                      <TableCell align='left'>
                        <MDBox className='text-truncate' sx={{ maxWidth: 200 }}>
                          {row.note || '-'}
                        </MDBox>
                      </TableCell>
                      <TableCell align='center' className='nowrap'>
                        {row.check_date} {row.check_time}
                      </TableCell>
                      <TableCell align='center'>{renderImage(row.image)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    const init = async () => {
      const res = await FetchData('GET', `/${mod}`, app.token, {
        lang: app.lang,
      });

      if (res.status === 'ok') {
        setCboBranch(res.branch || []);
        setCboCheckTypes(res.check_types || []);
        setCboChecklists(res.checklists || []);
        setUrlExcel(res.excel || '');
        view({ ...find });
      } else {
        Func.exitApp();
      }
    };

    init();
  }, [id]);

  return (
    <MDBox>
      <Navbar route={[t(`menu.account`), 'รายงานการตรวจสอบสินค้า']} />
      {renderList()}
      {renderImagePreview()}
      {renderLoading()}
    </MDBox>
  );
}

export default RpProductChecklist;
